import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Ban, CheckCircle2, Lock, Mail, AlertTriangle, CreditCard, ShieldCheck, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SunLogo } from "@/components/SunLogo";
import { formatDateTime, formatMoney } from "@/lib/format";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  AddressElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

interface SignerView {
  id: string;
  contractTitle: string;
  signerName: string;
  signerEmail: string;
  totalValueCents: number;
  depositAmountCents: number;
  signedAt: string | null;
  status: string;
  paidAt?: string | null;
  paidAmountCents?: number | null;
  paidChoice?: "deposit" | "full" | null;
  // Operator-facing Match No. (SR-XXXXXX, derived from the Stripe PI id)
  // surfaced on the receipt panel so the customer has a stable reference
  // if they need to ask about the payment later.
  stripeMatchNo?: string | null;
  // Refund state — cumulative amount refunded back to the card and the
  // most recent refund timestamp/id. Used to render a refund line on the
  // receipt and suppress the "remaining balance will be charged" message
  // when the customer is at or above the current contract total net.
  refundedAmountCents?: number | null;
  lastRefundAt?: string | null;
  lastRefundId?: string | null;
  // POR-side cancellation. When true, the contract has been cancelled or
  // voided in POR — we never accept a payment in that case, regardless of
  // the eSign-side "signed" status.
  porCancelled?: boolean;
  porStatusLabel?: string | null;
  // 14-day deposit policy. When `requireFullPayment` is true, the deposit
  // toggle is replaced with a single "Pay in full" panel and a non-
  // refundable explainer. `daysUntilContract` is whole UTC days from today
  // to the contract (HubSpot transaction_date); negative when the date is
  // already past. Both fields are optional — when they're absent the
  // customer page falls back to the legacy deposit/full toggle.
  requireFullPayment?: boolean;
  daysUntilContract?: number | null;
  contractDate?: string | null;
  // Account-customer sign-only flow. When false, the customer signed under
  // Net 30 terms and no payment is collected through this page. We render a
  // "no payment due" panel instead of the Stripe payment step.
  paymentRequired?: boolean;
  companyPorStatus?: string | null;
}

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : Promise.resolve(null);
  }
  return stripePromise;
}

export default function SignComplete() {
  const { token } = useParams<{ token: string }>();
  const { data } = useQuery<SignerView>({
    queryKey: [`/api/contracts/by-token/${token}`],
    staleTime: 0,
    refetchOnMount: "always",
  });

  const isPaid = !!data?.paidAt;
  // Sign-only / Net 30 contracts (On Account customers): no payment is
  // collected via this page. The signed PDF was already emailed; the
  // customer is shown a confirmation panel and told they'll be billed under
  // their account terms.
  const isSignOnly = data ? data.paymentRequired === false : false;

  const subhead = isPaid
    ? "Payment complete"
    : isSignOnly
    ? "Contract signed — Net 30 terms"
    : "Contract signed — payment required";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-primary"><SunLogo size={26} /></span>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold tracking-tight">Sun Rental</div>
              <div className="text-[11px] text-muted-foreground -mt-0.5">
                {subhead}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-2xl w-full mx-auto px-5 sm:px-6 py-10 md:py-16">
        {!data ? (
          <div className="text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.porCancelled ? (
          <CancelledNotice data={data} />
        ) : isSignOnly ? (
          <SignOnlyComplete data={data} token={token!} />
        ) : isPaid ? (
          <PaidSuccess data={data} token={token!} />
        ) : (
          <PaymentStep data={data} token={token!} />
        )}
      </div>

      <footer className="border-t border-border bg-card/40">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-5 text-[11px] text-muted-foreground text-center">
          <div>
            <span className="font-medium text-foreground">Sun Rental Center</span> · 7467 Mentor Ave, Mentor OH 44060
          </div>
          <div className="mt-0.5">
            440-942-6106 ·{" "}
            <a className="hover:underline" href="mailto:events@sunrental.com">
              events@sunrental.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Shown when POR has cancelled (or voided) the contract. Suppresses
// payment options entirely and tells the customer to contact the office.
// The signed PDF link is preserved so they have the record of what
// they signed before the cancellation, but no further charges are possible.
function CancelledNotice({ data }: { data: SignerView }) {
  const label = data.porStatusLabel || "Cancelled";
  return (
    <div className="text-center" data-testid="section-por-cancelled">
      <div className="h-14 w-14 rounded-full bg-destructive/10 text-destructive mx-auto flex items-center justify-center mb-4">
        <Ban className="h-7 w-7" />
      </div>
      <h1 className="text-3xl font-semibold tracking-tight">
        Contract {label.toLowerCase()}
      </h1>
      <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
        This contract has been {label.toLowerCase()} by Sun Rental and is no
        longer accepting payment. If you believe this is in error, please
        contact the rental office.
      </p>

      <div className="mt-8 rounded-xl border border-card-border bg-card p-5 md:p-6 text-left">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Contract</div>
        <div className="text-lg font-medium mt-1" data-testid="text-contract-title">
          {data.contractTitle}
        </div>
        {data.signedAt && (
          <div className="text-[11px] text-muted-foreground mt-2">
            Signed {formatDateTime(data.signedAt)}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-center">
        <a
          href={`/api/contracts/by-token/${encodeURIComponent(data.id)}/pdf?download=1`}
          onClick={(e) => {
            // Use the token from the URL, not data.id (PDF route is by-token).
            // This handler is intentionally a no-op — the href is patched
            // below where we have the actual token in scope.
            e.preventDefault();
          }}
          className="hidden"
        />
      </div>

      <div className="mt-8 text-[12px] text-muted-foreground">
        Sun Rental Center · (440) 255-2455
      </div>
    </div>
  );
}

function PaidSuccess({ data, token }: { data: SignerView; token: string }) {
  // Refund-aware receipt math. The contract row stores three independent
  // numbers: totalValueCents (signed contract amount, possibly updated by a
  // re-sign), paidAmountCents (what the customer actually paid at checkout),
  // and refundedAmountCents (cumulative amount returned to their card). The
  // page used to ignore refundedAmountCents, which caused two visible bugs:
  //   1. "Deposit met: $X" still showed the original X even after a
  //      partial refund — the customer's net paid was lower.
  //   2. "Remaining balance of $Y will be charged" went negative when a
  //      reduce-and-refund left paid > current total.
  // Net paid = max(0, paid − refunded). Remaining = max(0, total − net).
  const total = data.totalValueCents ?? 0;
  const paidGross = data.paidAmountCents ?? 0;
  const refunded = data.refundedAmountCents ?? 0;
  const netPaid = Math.max(0, paidGross - refunded);
  const remaining = Math.max(0, total - netPaid);
  const hasRefund = refunded > 0;
  // "Fully covered" = customer has net-paid the full current contract
  // amount within a 5% tolerance. Rental totals can drift slightly between
  // e-sign capture and the POR ledger (delivery fees, tax rounding, partial
  // returns), so anything inside ±5% counts as paid in full. After a
  // reduce-and-refund this also stays true when the customer paid the full
  // balance, items got removed, the delta was refunded, and net payment
  // matches the new lower total.
  const fullyCovered = total > 0 ? netPaid >= total * 0.95 : netPaid > 0;

  return (
    <>
      <div className="text-center">
        <div className="h-16 w-16 rounded-full bg-[hsl(var(--status-signed))]/12 text-[hsl(var(--status-signed))] mx-auto flex items-center justify-center mb-5">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="text-signed-headline">
          You're all set
        </h1>
        <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
          Thanks{data.signerName ? `, ${data.signerName.split(" ")[0]}` : ""}. Your contract is signed and your{" "}
          {fullyCovered ? "payment" : "deposit"} has been received.
          {hasRefund && (
            <>
              {" "}A refund of{" "}
              <span className="font-medium text-foreground tabular-nums">
                {formatMoney(refunded)}
              </span>{" "}
              has been issued to your card.
            </>
          )}
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-card-border bg-card p-5 md:p-6">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Receipt</div>
          {data.stripeMatchNo && (
            <div
              className="text-[11px] tabular-nums text-muted-foreground"
              data-testid="text-match-no"
            >
              Ref: <span className="font-medium text-foreground">{data.stripeMatchNo}</span>
            </div>
          )}
        </div>
        <div className="text-lg font-medium mt-1">{data.contractTitle}</div>
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total contract</div>
            <div className="text-sm font-medium tabular-nums mt-1" data-testid="text-total">{formatMoney(total)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {fullyCovered ? "Paid in full" : "Deposit met"}
            </div>
            <div
              className="text-sm font-medium tabular-nums mt-1 text-[hsl(var(--status-signed))]"
              data-testid="text-net-paid"
            >
              {formatMoney(netPaid)}
            </div>
            {hasRefund && (
              <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums" data-testid="text-paid-breakdown">
                {formatMoney(paidGross)} paid − {formatMoney(refunded)} refunded
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed at</div>
            <div className="text-sm font-medium mt-1">{formatDateTime(data.signedAt)}</div>
          </div>
        </div>

        {/* Refund line — prominent when the customer has been partially
            refunded but still has a remaining balance, so they understand
            the upcoming charge has already been adjusted. */}
        {hasRefund && (
          <div
            className="mt-5 rounded-md border border-[hsl(var(--status-signed))]/30 bg-[hsl(var(--status-signed))]/8 p-3 text-[12px] flex gap-2"
            data-testid="banner-refund-issued"
          >
            <RotateCcw className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--status-signed))]" />
            <div>
              <div className="font-medium text-foreground">
                Refund issued: {formatMoney(refunded)}
              </div>
              <div className="text-muted-foreground mt-0.5">
                {data.lastRefundAt && <>On {formatDateTime(data.lastRefundAt)}. </>}
                Refunds typically take 5–10 business days to appear on your statement.
              </div>
            </div>
          </div>
        )}

        {/* Future-charge notice — only when there's actually a remaining
            balance to charge. Hidden when fully covered (incl. the refund
            case) and never displays a negative number. */}
        {!fullyCovered && (
          <div
            className="mt-5 rounded-md bg-muted/40 border border-border p-3 text-[12px] text-muted-foreground"
            data-testid="banner-remaining-balance"
          >
            Remaining balance of{" "}
            <span className="font-medium text-foreground tabular-nums">
              {formatMoney(remaining)}
            </span>{" "}
            will be charged to your saved card before your event. We'll email a receipt when that runs.
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-5 text-sm text-muted-foreground flex gap-3">
        <Mail className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="text-foreground font-medium">A copy will be emailed</span> to you and to
          Sun Rental.
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> You may now close this window.
      </div>
    </>
  );
}

// Sign-only / Net 30 confirmation — shown after an On Account customer
// completes signing. No payment is taken through this page; the signed PDF
// was already emailed via Resend, and the salesperson handles billing under
// the customer's account terms.
function SignOnlyComplete({ data, token }: { data: SignerView; token: string }) {
  void token; // PDF link not surfaced here — the email already contains it.
  const total = data.totalValueCents ?? 0;
  return (
    <div data-testid="section-sign-only-complete">
      <div className="text-center">
        <div className="h-16 w-16 rounded-full bg-[hsl(var(--status-signed))]/12 text-[hsl(var(--status-signed))] mx-auto flex items-center justify-center mb-5">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="text-signed-headline">
          You're all set
        </h1>
        <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
          Thanks{data.signerName ? `, ${data.signerName.split(" ")[0]}` : ""}. Your contract is signed.
          No payment is required here — this order is billed under your
          company's Net 30 account terms.
        </p>
      </div>

      <div
        className="mt-8 rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 md:p-6"
        data-testid="panel-net30"
      >
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 font-medium">
            Net 30 — no payment due
          </div>
        </div>
        <div className="text-lg font-medium mt-1" data-testid="text-contract-title">
          {data.contractTitle}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total contract</div>
            <div className="text-sm font-medium tabular-nums mt-1" data-testid="text-total">
              {formatMoney(total)}
            </div>
          </div>
          {data.signedAt && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed at</div>
              <div className="text-sm font-medium mt-1">{formatDateTime(data.signedAt)}</div>
            </div>
          )}
        </div>
        <div className="mt-5 text-[12px] text-muted-foreground">
          Sun Rental will invoice your account. Payment terms are Net 30 from
          the invoice date. If you'd prefer to pay this order with a credit
          card instead, contact your sales rep and they'll send a payment
          link.
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-5 text-sm text-muted-foreground flex gap-3">
        <Mail className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="text-foreground font-medium">A signed copy has been emailed</span>{" "}
          to you and to Sun Rental.
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> You may now close this window.
      </div>
    </div>
  );
}

function PaymentStep({ data, token }: { data: SignerView; token: string }) {
  // When the contract is inside the 14-day window, the customer is locked
  // to pay-in-full and the toggle never appears. The server enforces this
  // independently on /payment-intent so there's no way to bypass it from
  // the UI.
  const requireFull = !!data.requireFullPayment;
  const [choice, setChoice] = useState<"deposit" | "full">(requireFull ? "full" : "deposit");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentAmount, setIntentAmount] = useState<number>(data.depositAmountCents);
  const [retryBanner, setRetryBanner] = useState<string | null>(null);
  const [initialError, setInitialError] = useState<string | null>(null);
  // Lifted from PayForm so we can lock the deposit↔full toggle while the
  // PaymentIntent is being confirmed. Toggling mid-confirm previously caused
  // a race where the "loser" choice's PI succeeded but the contract row was
  // updated to the new choice, producing a charge for the wrong amount
  // labeled with the wrong choice in the dashboard.
  const [paySubmitting, setPaySubmitting] = useState(false);

  const intentMutation = useMutation({
    mutationFn: async (c: "deposit" | "full") => {
      const res = await apiRequest("POST", `/api/contracts/by-token/${token}/payment-intent`, {
        choice: c,
      });
      return (await res.json()) as { clientSecret: string; amount: number; choice: "deposit" | "full" };
    },
    onSuccess: (resp) => {
      setClientSecret(resp.clientSecret);
      setIntentAmount(resp.amount);
      setInitialError(null);
    },
    onError: (err: any) => {
      setInitialError(err?.message || "Could not start payment.");
    },
  });

  // Create / refresh PI when choice changes
  useEffect(() => {
    setClientSecret(null);
    intentMutation.mutate(choice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice]);

  const stripeP = useMemo(() => getStripe(), []);
  const remaining = data.totalValueCents - (data.depositAmountCents ?? 0);

  return (
    <>
      <div className="text-center">
        <div className="h-14 w-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center mb-4">
          <CreditCard className="h-7 w-7" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">One more step — payment</h1>
        <p className="text-base text-muted-foreground mt-3 max-w-md mx-auto">
          Your contract is signed. Choose how much to pay now to confirm your reservation.
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-card-border bg-card p-5 md:p-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Contract</div>
        <div className="text-lg font-medium mt-1">{data.contractTitle}</div>
        {/* Inside 14d the entire total is due + non-refundable, so the
            "Non-refundable deposit" tile is suppressed — the dedicated
            pay-in-full panel below states the total is non-refundable. */}
        <div className={"grid gap-4 mt-5 " + (requireFull ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3") }>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</div>
            <div className="text-sm font-medium tabular-nums mt-1">{formatMoney(data.totalValueCents)}</div>
          </div>
          {!requireFull && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Non-refundable deposit</div>
              <div className="text-sm font-medium tabular-nums mt-1">{formatMoney(data.depositAmountCents)}</div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed</div>
            <div className="text-sm font-medium mt-1">{formatDateTime(data.signedAt)}</div>
          </div>
        </div>
      </div>

      {/* Forced pay-in-full panel — contract is within 14 days of the
          customer's event, so the deposit option is suppressed and the
          payment is non-refundable. */}
      {requireFull && (
        <div className="mt-6">
          <div
            className="rounded-xl border border-primary bg-primary/5 ring-1 ring-primary p-5"
            data-testid="panel-pay-in-full-required"
          >
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Pay in full — non-refundable
            </div>
            <div className="text-2xl font-semibold tabular-nums mt-1" data-testid="text-required-amount">
              {formatMoney(data.totalValueCents)}
            </div>
            <div className="text-[12px] text-muted-foreground mt-2">
              Your contract date is within 14 days, so the full amount is due
              now and is non-refundable.
            </div>
          </div>
        </div>
      )}

      {/* Choice toggle. Locked while a payment is being confirmed so the
          customer can't race deposit↔full mid-charge. Hidden entirely
          when pay-in-full is required. */}
      {!requireFull && (
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => !paySubmitting && setChoice("deposit")}
          disabled={paySubmitting}
          className={
            "rounded-xl border p-4 text-left transition " +
            (paySubmitting ? "opacity-60 cursor-not-allowed " : "") +
            (choice === "deposit"
              ? "border-primary bg-primary/5 ring-1 ring-primary"
              : "border-border hover:bg-muted/40")
          }
          data-testid="button-choice-deposit"
        >
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">50% non-refundable deposit</div>
          <div className="text-xl font-semibold tabular-nums mt-1">{formatMoney(data.depositAmountCents)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Remaining {formatMoney(remaining)} charged to your card before the event.
          </div>
        </button>
        <button
          type="button"
          onClick={() => !paySubmitting && setChoice("full")}
          disabled={paySubmitting}
          className={
            "rounded-xl border p-4 text-left transition " +
            (paySubmitting ? "opacity-60 cursor-not-allowed " : "") +
            (choice === "full"
              ? "border-primary bg-primary/5 ring-1 ring-primary"
              : "border-border hover:bg-muted/40")
          }
          data-testid="button-choice-full"
        >
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pay in full</div>
          <div className="text-xl font-semibold tabular-nums mt-1">{formatMoney(data.totalValueCents)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Settle the entire balance now. No further charges.
          </div>
        </button>
      </div>
      )}

      {/* Retry banner from a prior failure */}
      {retryBanner && (
        <div
          className="mt-5 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/8 text-[hsl(var(--destructive))] p-3 flex gap-3 text-sm"
          data-testid="banner-payment-error"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Payment didn't go through</div>
            <div className="opacity-90">{retryBanner} Please review the details and try again.</div>
          </div>
        </div>
      )}

      {/* Stripe Payment Element */}
      <div className="mt-6 rounded-xl border border-card-border bg-card p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium">Card details</div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secured by Stripe
          </div>
        </div>

        {!PUBLISHABLE_KEY && (
          <div className="text-sm text-[hsl(var(--destructive))]">
            Stripe publishable key is missing. Add VITE_STRIPE_PUBLISHABLE_KEY to enable payments.
          </div>
        )}

        {initialError && (
          <div className="text-sm text-[hsl(var(--destructive))] mb-3">{initialError}</div>
        )}

        {clientSecret && PUBLISHABLE_KEY ? (
          <Elements
            // remount when the clientSecret changes (e.g. after switching choice)
            key={clientSecret}
            stripe={stripeP}
            options={{
              clientSecret,
              appearance: { theme: "stripe" },
            }}
          >
            <PayForm
              token={token}
              amount={intentAmount}
              choice={choice}
              signerName={data.signerName}
              signerEmail={data.signerEmail}
              onSubmittingChange={setPaySubmitting}
              onFailure={(msg) => setRetryBanner(msg)}
              onSuccess={() => {
                setRetryBanner(null);
                queryClient.invalidateQueries({ queryKey: [`/api/contracts/by-token/${token}`] });
              }}
            />
          </Elements>
        ) : (
          <div className="h-32 rounded-md bg-muted/30 animate-pulse" />
        )}
      </div>

      <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> Card details are submitted directly to Stripe and never touch our servers.
      </div>
    </>
  );
}

function PayForm({
  token,
  amount,
  choice,
  signerName,
  signerEmail,
  onSuccess,
  onFailure,
  onSubmittingChange,
}: {
  token: string;
  amount: number;
  choice: "deposit" | "full";
  signerName: string;
  signerEmail: string;
  onSuccess: () => void;
  onFailure: (msg: string) => void;
  onSubmittingChange?: (v: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const setSubmittingBoth = (v: boolean) => {
    setSubmitting(v);
    onSubmittingChange?.(v);
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmittingBoth(true);
    setInlineError(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        // Ensure the billing details from <AddressElement> + name/email
        // flow through to the PaymentMethod for AVS / chargeback protection.
        payment_method_data: {
          billing_details: {
            name: signerName,
            email: signerEmail,
          },
        },
      },
    });

    if (error) {
      const msg = error.message || "Card was declined.";
      setInlineError(msg);
      onFailure(msg);
      setSubmittingBoth(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      try {
        await apiRequest("POST", `/api/contracts/by-token/${token}/payment-confirm`);
        onSuccess();
      } catch (err: any) {
        const msg = err?.message || "Payment succeeded but we couldn't finalize. Please contact us.";
        setInlineError(msg);
        onFailure(msg);
      }
    } else {
      const msg = `Payment is ${paymentIntent?.status || "not yet complete"}. Please try again.`;
      setInlineError(msg);
      onFailure(msg);
    }
    setSubmittingBoth(false);
  };

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Billing address
        </div>
        <AddressElement
          options={{
            mode: "billing",
            defaultValues: {
              name: signerName,
            },
            fields: { phone: "always" },
            validation: { phone: { required: "always" } },
          }}
        />
      </div>
      <div className="rounded-md border border-border bg-card p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Card details
        </div>
        <PaymentElement
          options={{
            layout: "tabs",
            // Hide billing fields inside the PaymentElement — we collect them
            // explicitly above with <AddressElement>.
            fields: {
              billingDetails: {
                name: "never",
                email: "never",
                phone: "never",
                address: "never",
              },
            },
            wallets: { applePay: "never", googlePay: "never", link: "never" },
            terms: { card: "never" },
          }}
        />
      </div>
      {inlineError && (
        <div className="text-sm text-[hsl(var(--destructive))]" data-testid="text-pay-inline-error">
          {inlineError}
        </div>
      )}
      <Button
        type="submit"
        className="w-full h-11 text-base"
        disabled={!stripe || !elements || submitting}
        data-testid="button-pay"
      >
        {submitting
          ? "Processing…"
          : `Pay ${formatMoney(amount)} ${choice === "full" ? "(full balance)" : "(deposit)"}`}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
        <ShieldCheck className="h-3 w-3 inline-block mr-1 -mt-0.5" />
        Your billing address is verified against your card to protect against fraud.
      </p>
    </form>
  );
}
